import type { ReactNode } from 'react';
import { Link } from 'react-router';
import {
  ArrowUpRight,
  CircleCheck,
  CircleDot,
  GitBranch,
  GitCommitHorizontal,
  Lock,
  Rocket,
  Users,
} from 'lucide-react';
import type { ProjectActivityMetric, ProjectView } from '@vwt/contracts';
import { Avatar, Badge, Tooltip } from '@vwt/ui';
import { useTranslation } from '../../lib/i18n.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import { TimeAgo } from '../../components/common.js';
import { ProjectStatusBadge } from './project-updates.js';
import { OpenInVammoApps, VammoAppBadge } from './vammo-app.js';

function Metric({
  icon,
  label,
  value,
  detail,
  hint,
  tone = '',
}: {
  icon: ReactNode;
  label: string;
  value: number | null;
  detail: ReactNode;
  hint: string;
  tone?: string;
}) {
  return (
    <Tooltip content={hint}>
      <div
        tabIndex={0}
        className="min-w-0 rounded-md px-2 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <dt className="flex items-center gap-1.5 text-xs text-fg-muted">
          {icon}
          <span>{label}</span>
        </dt>
        <dd className={`mt-1 text-2xl font-semibold tracking-tight tabular-nums ${tone}`}>
          {value === null ? '—' : formatNumber(value)}
        </dd>
        <dd className="mt-1 text-xs text-fg-subtle">{detail}</dd>
      </div>
    </Tooltip>
  );
}

export function ProjectListRow({ project: p }: { project: ProjectView }) {
  const { t } = useTranslation();
  const a = p.activity;
  const technical = a.commitsThisWeek.coverage !== 'FORBIDDEN';
  const sourceHint = (metric: ProjectActivityMetric, kind: 'commits' | 'deployments') =>
    [
      t(`projects.activity.${kind}Hint`),
      t(`projects.activity.coverage.${metric.coverage}`),
      metric.lastSyncedAt
        ? t('projects.activity.lastSync', { date: formatDateTime(metric.lastSyncedAt) })
        : '',
    ]
      .filter(Boolean)
      .join(' ');
  return (
    <li className="group rounded-xl border border-border bg-surface shadow-xs transition-colors hover:border-border-strong">
      <article className="grid min-w-0 gap-5 p-4 md:p-5 xl:grid-cols-[minmax(240px,1fr)_minmax(0,1.8fr)] xl:items-center">
        <div className="min-w-0">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-xs font-bold tracking-wide text-accent-soft-fg"
            >
              {p.key.slice(0, 3)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="min-w-0 text-base font-semibold tracking-tight">
                  <Link
                    to={`/projects/${p.id}`}
                    className="rounded-sm break-words hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {p.name}
                  </Link>
                </h2>
                {p.visibility === 'RESTRICTED' ? (
                  <Lock
                    className="size-3.5 shrink-0 text-warning"
                    aria-label={t('status.visibility.RESTRICTED')}
                  />
                ) : null}
                <Link
                  to={`/projects/${p.id}`}
                  aria-label={t('projects.activity.openProject', { name: p.name })}
                  className="ml-auto rounded-md p-1 text-fg-subtle hover:bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ArrowUpRight className="size-4" />
                </Link>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
                <span className="font-mono">{p.key}</span>
                {p.kind === 'VAMMO_APP' ? <VammoAppBadge /> : null}
                {p.latestUpdate ? <ProjectStatusBadge status={p.latestUpdate.status} /> : null}
                {p.archivedAt ? <Badge>{t('projects.activity.archived')}</Badge> : null}
              </div>
            </div>
          </div>
          {p.descriptionMarkdown ? (
            <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-fg-muted">
              {p.descriptionMarkdown.replace(/[*_`#>]/g, '')}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-fg-subtle">
            {p.owningTeams.map((team) => (
              <Link
                key={team.id}
                to={`/teams/${team.id}`}
                className="hover:text-fg hover:underline"
              >
                {team.name}
              </Link>
            ))}
            <span
              className="inline-flex items-center gap-1"
              title={p.repositories.map((r) => r.fullName).join('\n')}
            >
              <GitBranch className="size-3" />
              {t('projects.activity.repositoryCount', { count: p.repositories.length })}
            </span>
            {p.managers.length ? (
              <span className="flex items-center -space-x-1" aria-label={t('projects.managers')}>
                {p.managers.slice(0, 3).map((m) => (
                  <Tooltip key={m.id} content={`${t('projects.managers')}: ${m.displayName}`}>
                    <span tabIndex={0} className="rounded-full ring-2 ring-surface">
                      <Avatar name={m.displayName} size="sm" />
                    </span>
                  </Tooltip>
                ))}
                {p.managers.length > 3 ? (
                  <span className="pl-2">+{formatNumber(p.managers.length - 3)}</span>
                ) : null}
              </span>
            ) : null}
          </div>
          {p.vammoApp ? (
            <div className="mt-2 -ml-2">
              <OpenInVammoApps project={p} compact />
            </div>
          ) : null}
          <p className="mt-3 text-xs text-fg-subtle">
            {a.lastActivityAt ? (
              <>
                {t('projects.activity.lastActivity')} <TimeAgo iso={a.lastActivityAt} />
              </>
            ) : (
              t('projects.activity.noActivity')
            )}
          </p>
        </div>
        <dl
          className={`grid grid-cols-2 gap-x-2 gap-y-3 border-t border-border pt-3 sm:grid-cols-3 xl:border-t-0 xl:border-l xl:pt-0 xl:pl-4 ${technical ? 'xl:grid-cols-5' : ''}`}
        >
          <Metric
            icon={<CircleDot className="size-3.5 shrink-0" />}
            label={t('projects.activity.open')}
            value={p.openWorkCount}
            detail={
              <>
                <span>{t('projects.activity.inProgress', { count: a.inProgressWorkCount })}</span>
                {a.blockedWorkCount > 0 ? (
                  <span className="mt-1 block font-medium text-warning">
                    {t('projects.activity.blocked', { count: a.blockedWorkCount })}
                  </span>
                ) : null}
              </>
            }
            hint={t('projects.activity.openHint')}
          />
          <Metric
            icon={<CircleCheck className="size-3.5 shrink-0" />}
            label={t('projects.activity.completed')}
            value={a.completedThisWeek}
            tone="text-success"
            detail={
              <>
                {t('projects.activity.thisWeek')}
                {a.importedCompletedThisWeek > 0 ? (
                  <span className="mt-1 block">
                    {t('projects.activity.imported', { count: a.importedCompletedThisWeek })}
                  </span>
                ) : null}
              </>
            }
            hint={t('projects.activity.completedHint')}
          />
          {technical ? (
            <>
              <Metric
                icon={<GitCommitHorizontal className="size-3.5 shrink-0" />}
                label={t('projects.activity.commits')}
                value={a.commitsThisWeek.count}
                detail={t(`projects.activity.coverage.${a.commitsThisWeek.coverage}`)}
                hint={sourceHint(a.commitsThisWeek, 'commits')}
              />
              <Metric
                icon={<Rocket className="size-3.5 shrink-0" />}
                label={t('projects.activity.deployments')}
                value={a.deploymentsThisWeek.count}
                detail={t(`projects.activity.coverage.${a.deploymentsThisWeek.coverage}`)}
                hint={sourceHint(a.deploymentsThisWeek, 'deployments')}
              />
            </>
          ) : null}
          <Metric
            icon={<Users className="size-3.5 shrink-0" />}
            label={t('projects.activity.activePeople')}
            value={a.activeUsersThisWeek}
            detail={t('projects.activity.thisWeek')}
            hint={t('projects.activity.peopleHint')}
          />
        </dl>
      </article>
    </li>
  );
}
